import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useAuth } from "../contexts/AuthContext";
import { updateCompanyInfo } from "../api/user";
import {
  ChevronUpDownIcon,
  CheckIcon,
  PlusIcon,
  LockClosedIcon,
  ArrowUpCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import CompanyAvatar from "./CompanyAvatar";
import PhoneInput, { isValidPhone } from "./PhoneInput";
import { AnimatedModal, Input, Button } from "./ui";

// ── Plan limit helper ─────────────────────────────────────────────────────────
// Returns the max companies allowed by the given planId.
// -1 = unlimited.
const getPlanCompanyLimit = (planId) => {
  const limits = {
    FREE: 1,
    FREE_IN: 1,
    STANDARD: 1,
    STANDARD_IN: 1,
    BUSINESS: 1,
    BUSINESS_IN: 1,
    BUSINESS_PRO: -1,
    BUSINESS_PRO_IN: -1,
    GROUP_STARTER: 3,
    GROUP_STARTER_IN: 3,
    GROUP_PRO: 10,
    GROUP_PRO_IN: 10,
    ENTERPRISE_HOLDING: -1,
    ENTERPRISE_HOLDING_IN: -1,
  };
  return limits[planId] ?? 1;
};

// Returns the next recommended plan name to suggest when user hits their limit
const getUpgradeSuggestion = (planId) => {
  if (
    !planId ||
    [
      "FREE",
      "STANDARD",
      "BUSINESS",
      "FREE_IN",
      "STANDARD_IN",
      "BUSINESS_IN",
    ].includes(planId)
  ) {
    return {
      name: "Group Starter",
      description: "Manage up to 3 companies under one organization.",
    };
  }
  if (["GROUP_STARTER", "GROUP_STARTER_IN"].includes(planId)) {
    return {
      name: "Group Pro",
      description: "Scale up to 10 companies with a shared sign-request pool.",
    };
  }
  if (["GROUP_PRO", "GROUP_PRO_IN"].includes(planId)) {
    return {
      name: "Enterprise",
      description:
        "Unlimited companies with custom limits and dedicated support.",
    };
  }
  return null;
};

const addCompanySchema = Yup.object({
  name: Yup.string().required("Company name is required"),
  address: Yup.string().notRequired(),
  phone: Yup.string()
    .test(
      "phone",
      "Enter a valid phone number (6–15 digits)",
      (v) => !v || isValidPhone(v),
    )
    .notRequired(),
});

// ── Add Company Modal ─────────────────────────────────────────────────────────
const AddCompanyModal = ({ isOpen, onClose, onSuccess }) => {
  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-white w-full max-w-md overflow-hidden rounded-xl shadow-md border border-gray-200"
      zIndex="z-[9999]"
      closeOnBackdrop={true}
    >
      <div className="flex items-center justify-between px-6 py-3">
        <h3 className="font-heading text-xl font-semibold text-gray-900">
          Add New Company
        </h3>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors duration-200"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <Formik
        initialValues={{ name: "", address: "", phone: "" }}
        validationSchema={addCompanySchema}
        onSubmit={async (values, { setSubmitting, setFieldError }) => {
          try {
            await updateCompanyInfo({
              companyInfo: {
                name: values.name,
                address: values.address || undefined,
                phone: values.phone || undefined,
              },
            });
            onSuccess();
          } catch (err) {
            setFieldError(
              "name",
              err?.response?.data?.message || "Failed to create company",
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="px-6 py-5 space-y-5">
            <Input
              label="Company Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. Acme Ltd."
              required
              error={errors.name && touched.name ? errors.name : undefined}
            />
            <Input
              label="Address"
              name="address"
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Company address (optional)"
            />
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <PhoneInput
                name="phone"
                value={values.phone}
                onChange={(v) => setFieldValue("phone", v)}
                onBlur={handleBlur}
                error={errors.phone && touched.phone}
              />
              {errors.phone && touched.phone && (
                <p className="mt-1.5 text-xs font-medium text-danger">
                  {errors.phone}
                </p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating…" : "Create Company"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </AnimatedModal>
  );
};

// ── Plan Limit / Upgrade Modal ────────────────────────────────────────────────
const PlanLimitModal = ({
  isOpen,
  onClose,
  planId,
  companiesCount,
  maxCompanies,
  onViewPlans,
}) => {
  const suggestion = getUpgradeSuggestion(planId);
  const isSinglePlan = [
    "FREE",
    "STANDARD",
    "BUSINESS",
    "FREE_IN",
    "STANDARD_IN",
    "BUSINESS_IN",
  ].includes(planId);

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-white w-full max-w-md overflow-hidden"
      zIndex="z-[9999]"
      closeOnBackdrop={true}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <h3 className="font-heading text-lg font-semibold text-gray-900">
          {isSinglePlan
            ? "Multi-Company Requires an Upgrade"
            : "Company Limit Reached"}
        </h3>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="px-5 py-5 space-y-4">
        {/* Current usage */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <LockClosedIcon className="w-5 h-5 text-amber-500 shrink-0" />
          <div>
            {isSinglePlan ? (
              <p className="text-sm text-amber-800">
                Your current plan only supports{" "}
                <span className="font-semibold">1 company</span>. Upgrade to a
                Group plan to manage multiple companies under one organization.
              </p>
            ) : (
              <p className="text-sm text-amber-800">
                You are using{" "}
                <span className="font-semibold">
                  {companiesCount} of {maxCompanies} companies
                </span>{" "}
                allowed on your current plan.
              </p>
            )}
          </div>
        </div>

        {/* Suggested next plan */}
        {suggestion && (
          <div className="border border-primary/30 rounded-lg p-4 bg-primary/5">
            <div className="flex items-start gap-3">
              <ArrowUpCircleIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Upgrade to {suggestion.name}
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  {suggestion.description}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onViewPlans}
            className="flex-1 py-2 px-4 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90"
          >
            View Plans
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

// ── CompanySwitcher ───────────────────────────────────────────────────────────
const CompanySwitcher = ({ collapsed = false }) => {
  const {
    companies,
    activeCompany,
    switchCompany,
    organization,
    fetchUserCompanies,
  } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const handleSwitch = async (companyId) => {
    if (companyId === activeCompany?._id) {
      setIsModalOpen(false);
      return;
    }
    await switchCompany(companyId);
    setIsModalOpen(false);
    window.dispatchEvent(
      new CustomEvent("company-switched", { detail: { companyId } }),
    );
  };

  // Only ORG_ADMIN users see "Add Company"
  const isOrgAdmin = companies?.some((c) => c.isOrgAdmin) ?? false;

  // Plan-based limits — only owned companies count against the limit
  const planId = organization?.planId;
  const maxCompanies =
    organization?.allowedCompanies ?? getPlanCompanyLimit(planId);
  const unlimited = maxCompanies === -1;
  const ownedCount = companies?.filter((c) => c.isOrgAdmin).length ?? 0;
  const currentCount = ownedCount;
  const atLimit = !unlimited && currentCount >= maxCompanies;

  const handleAddCompanyClick = () => {
    setIsModalOpen(false);
    if (atLimit || !isOrgAdmin) {
      setShowLimitModal(true);
    } else {
      setShowAddModal(true);
    }
  };

  const handleAddSuccess = async () => {
    setShowAddModal(false);
    await fetchUserCompanies();
    // Auto-switch to the new (last) company after refresh
    window.location.reload();
  };

  const handleViewPlans = () => {
    setShowLimitModal(false);
    navigate("/dashboard/organization/subscription");
  };

  if (!companies || companies.length === 0) return null;

  const ownedCompanies = companies.filter((c) => c.isOrgAdmin);
  const invitedCompanies = companies.filter((c) => !c.isOrgAdmin);

  const renderCompany = (company) => {
    const isActive = activeCompany?._id === company._id;
    const orgName = company.organization?.name;
    const roleName = company.isOrgAdmin
      ? "Org Admin"
      : company.membership?.role?.name ||
        (company.membership?.companyRole === "MEMBER"
          ? "Member"
          : company.membership?.companyRole) ||
        null;
    return (
      <li key={company._id}>
        <button
          onClick={() => handleSwitch(company._id)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors duration-200 text-left ${
            isActive
              ? "bg-primary/10 text-primary"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          <CompanyAvatar company={company} size="xs" />
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{company.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {orgName && (
                <span className="text-xs text-gray-500 truncate max-w-[140px]">
                  {orgName}
                </span>
              )}
              {orgName && roleName && (
                <span className="text-gray-300 text-xs">·</span>
              )}
              {roleName && (
                <span
                  className={`text-xs font-medium px-1.5 py-0.5 rounded-md shrink-0 ${
                    company.isOrgAdmin
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {roleName}
                </span>
              )}
            </div>
          </div>
          {isActive && <CheckIcon className="w-5 h-5 text-primary shrink-0" />}
        </button>
      </li>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`w-full flex items-center gap-3 text-sm transition-all duration-200 ${
          collapsed
            ? "justify-center p-0"
            : "rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 px-3 py-3 justify-between"
        }`}
        title={collapsed ? activeCompany?.name || "Select Company" : undefined}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 overflow-hidden ${collapsed ? "" : "rounded-lg ring-1 ring-gray-100"}`}>
            <CompanyAvatar company={activeCompany} size={collapsed ? "sm" : "xs"} />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1 text-left">
              <p className="font-semibold text-gray-900 truncate max-w-[140px]">
                {activeCompany?.name || "Select Company"}
              </p>
              {organization && (
                <p className="text-xs text-gray-500 truncate max-w-[140px] mt-0.5">
                  {organization.name}
                </p>
              )}
            </div>
          )}
        </div>
        {!collapsed && (
          <ChevronUpDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* Companies Modal — portal to body for full-screen overlay */}
      {ReactDOM.createPortal(
        <AnimatedModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          contentClassName="bg-white w-full max-w-md overflow-hidden flex flex-col max-h-[85vh]"
          closeOnBackdrop={true}
          zIndex="z-[9999]"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 className="font-heading text-lg font-semibold text-gray-900">
              Switch Company
            </h3>
            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Org header + company count */}
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600 truncate">
              {organization?.name || "My Organization"}
            </p>
            {isOrgAdmin && !unlimited && (
              <span
                className={`text-xs font-medium px-2 py-1 rounded-lg shrink-0 ${
                  atLimit
                    ? "bg-amber-100 text-amber-700"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {currentCount}/{maxCompanies}
              </span>
            )}
            {isOrgAdmin && unlimited && (
              <span className="text-xs font-medium px-2 py-1 rounded-lg shrink-0 bg-primary/10 text-primary">
                Unlimited
              </span>
            )}
          </div>

          {/* Company list — scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4">
            {ownedCompanies.length > 0 && (
              <div className="mb-4">
                <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  My Companies
                </p>
                <ul className="space-y-1">
                  {ownedCompanies.map(renderCompany)}
                </ul>
              </div>
            )}
            {invitedCompanies.length > 0 && (
              <div>
                {ownedCompanies.length > 0 && (
                  <div className="border-t border-gray-100 my-4" />
                )}
                <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Invited
                </p>
                <ul className="space-y-1">
                  {invitedCompanies.map(renderCompany)}
                </ul>
              </div>
            )}
          </div>

          {/* Add Company footer */}
          {isOrgAdmin && (
            <div className="border-t border-gray-200 px-4 py-3">
              <button
                onClick={handleAddCompanyClick}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  atLimit
                    ? "text-amber-700 bg-amber-50 hover:bg-amber-100"
                    : "text-primary bg-primary/10 hover:bg-primary/15"
                }`}
              >
                {atLimit ? (
                  <LockClosedIcon className="w-5 h-5 shrink-0" />
                ) : (
                  <PlusIcon className="w-5 h-5 shrink-0" />
                )}
                <span>{atLimit ? "Upgrade to add more" : "Add Company"}</span>
              </button>
            </div>
          )}
        </AnimatedModal>,
        document.body,
      )}

      {/* Add Company Form Modal — portal for full-screen */}
      {ReactDOM.createPortal(
        <AddCompanyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />,
        document.body,
      )}

      {/* Plan Limit / Upgrade Modal — portal for full-screen */}
      {ReactDOM.createPortal(
        <PlanLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          planId={planId}
          companiesCount={currentCount}
          maxCompanies={maxCompanies}
          onViewPlans={handleViewPlans}
        />,
        document.body,
      )}
    </>
  );
};

export default CompanySwitcher;
